import { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import formatDateShort from "@/lib/dateFormatter";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

export default function AdminAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [recipientType, setRecipientType] = useState("all");
  const [selectedClients, setSelectedClients] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const [announcementsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/announcements`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/clientsAll`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setAnnouncements(announcementsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      const token = localStorage.getItem("token");
      const data = {
        title,
        content,
        recipient_type: recipientType,
        recipient_ids: recipientType === "selected" ? selectedClients : null,
      };

      await axios.post(`${API}/announcements`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Announcement sent successfully");
      setOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to send announcement");
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setTitle("");
    setContent("");
    setRecipientType("all");
    setSelectedClients([]);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          Loading...
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
          <div>
            <h1 className="text-4xl font-black text-slate-900">
              Announcements
            </h1>
            <p className="text-slate-600 mt-2">
              Send announcements to your clients
            </p>
          </div>
          <Dialog
            open={open}
            onOpenChange={(val) => {
              setOpen(val);
              if (!val) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button
                className="bg-indigo-700 hover:bg-indigo-800 gap-2"
                data-testid="create-announcement-btn"
              >
                <Plus className="w-4 h-4" />
                New Announcement
              </Button>
            </DialogTrigger>

            <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden">
              <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
                <DialogTitle>Create Announcement</DialogTitle>
              </DialogHeader>
              <div className="px-6 pb-6">
                <form
                  onSubmit={handleSend}
                  className="space-y-4"
                  data-testid="announcement-form"
                >
                  <div className="relative">
                    {loading && (
                      <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
                        <div className="flex items-center gap-2 text-blue-700 font-semibold">
                          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          Saving client...
                        </div>
                      </div>
                    )}

                    <div>
                      <Label>Title</Label>
                      <Input
                        data-testid="announcement-title-input"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label>Message</Label>
                      <Textarea
                        data-testid="announcement-content-input"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        rows={5}
                        required
                      />
                    </div>
                    <div>
                      <Label>Send To</Label>
                      <Select
                        value={recipientType}
                        onValueChange={setRecipientType}
                      >
                        <SelectTrigger data-testid="announcement-recipient-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Clients</SelectItem>
                          <SelectItem value="selected">
                            Selected Clients
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {recipientType === "selected" && (
                      <div>
                        <Label>Select Clients</Label>
                        <Select
                          value={selectedClients[0] || ""}
                          onValueChange={(val) => setSelectedClients([val])}
                        >
                          <SelectTrigger data-testid="announcement-client-select">
                            <SelectValue placeholder="Choose client" />
                          </SelectTrigger>
                          <SelectContent>
                            {clients.map((client) => (
                              <SelectItem key={client.id} value={client.id}>
                                {client.full_name} ({client.email})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <Button
                      type="submit"
                      disabled={sending}
                      className="w-full bg-indigo-700 hover:bg-indigo-800  text-[20px] font-semibold h-14 mt-2"
                      data-testid="announcement-submit-btn"
                    >
                      {sending ? "Sending..." : "Send Announcement"}
                    </Button>
                  </div>
                </form>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-4">
          {announcements.length === 0 ? (
            <div className="bg-white border border-slate-200 p-8 text-center text-slate-500">
              No announcements yet
            </div>
          ) : (
            announcements.map((announcement) => (
              <div
                key={announcement.id}
                className="bg-white border border-slate-200 p-6 mb-2 rounded-lg"
                data-testid={`announcement-${announcement.id}`}
              >
                <h3 className="text-xl font-bold text-slate-900">
                  {announcement.title}
                </h3>
                <p className="text-slate-600 mb-1">{announcement.content}</p>
                <p className="text-xs text-slate-500">
                  Sent: {formatDateShort(announcement.created_at)}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
